import React, { useEffect, useState, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import { pick } from "lodash";
import { connect } from "react-redux";

import { Button, Modal, Form, Grid, Icon, Message, Dropdown, Segment } from "semantic-ui-react";
import { Store as NotificationStore } from "react-notifications-component";
import Api from "@Api";

import Editor from "@monaco-editor/react";
import IkTable from "@Components/IkTable";
import Screen from "@Components/Screen";
import ConfirmModal from "@Modals/ConfirmModal";

import styles from "./styles.module.css";

const JSON_FIELDS = ["flow", "settings"];

const CATEGORY_OPTIONS = [
	{ key: "SIGN_UP", text: "Sign Up", value: "SIGN_UP" },
	{ key: "SIGN_IN", text: "Sign In", value: "SIGN_IN" },
	{ key: "APPOINTMENT_BOOKING", text: "Appointment Booking", value: "APPOINTMENT_BOOKING" },
	{ key: "LEAD_GENERATION", text: "Lead Generation", value: "LEAD_GENERATION" },
	{ key: "SHOPPING", text: "Shopping", value: "SHOPPING" },
	{ key: "CONTACT_US", text: "Contact Us", value: "CONTACT_US" },
	{ key: "CUSTOMER_SUPPORT", text: "Customer Support", value: "CUSTOMER_SUPPORT" },
	{ key: "SURVEY", text: "Survey", value: "SURVEY" },
	{ key: "OTHER", text: "Other", value: "OTHER" },
];

const notify = (message, type = "success") => {
	NotificationStore.addNotification({
		type,
		title: type === "danger" ? "Error" : "Éxito",
		message,
		insert: "top",
		container: "top-right",
		animationIn: ["animate__animated animate__fadeInRight"],
		animationOut: ["animate__animated animate__fadeOutRight"],
		dismiss: { duration: 4000, onScreen: true, pauseOnHover: true },
	});
};

const emptyFlow = {
	flowId: "",
	isActive: true,
	name: "",
	label: "",
	channelId: "",
	endpoint_uri: "",
	categories: [],
	settings: "{}",
	flow: "{}",
};

const stringifyJson = (val) => {
	if (!val) return "";
	try {
		return typeof val === "string" ? val : JSON.stringify(val, null, 2);
	} catch {
		return String(val);
	}
};

const validateJsonFields = (data) => {
	for (const field of JSON_FIELDS) {
		if (typeof data[field] === "string" && data[field].trim()) {
			try {
				JSON.parse(data[field]);
			} catch {
				return `Campo "${field}" tiene JSON inválido`;
			}
		}
	}
	return null;
};

const parseCategories = (raw) => {
	if (Array.isArray(raw)) return raw;
	try {
		return JSON.parse(raw || "[]");
	} catch {
		return [];
	}
};

const FlowModal = ({ open, onClose, flow, mode, onSave, channelsData }) => {
	const isView = mode === "view";
	const isCreate = mode === "create";
	const isEdit = mode === "edit";
	const canEdit = (field) => {
		if (isView) return false;
		if (isEdit) return field === "flow" || field === "isActive";
		return isCreate;
	};
	const [form, setForm] = useState(emptyFlow);
	const [error, setError] = useState(null);
	const [saving, setSaving] = useState(false);
	const [whatsappOptions, setWhatsappOptions] = useState([]);
	const [editorFullscreen, setEditorFullscreen] = useState(false);
	const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);

	const hasUnsavedData = () => {
		if (!isCreate) return false;
		if (form.name && form.name.trim()) return true;
		if (form.label && form.label.trim()) return true;
		if (form.channelId) return true;
		if (form.endpoint_uri && form.endpoint_uri.trim()) return true;
		if (Array.isArray(form.categories) && form.categories.length > 0) return true;
		if (form.flow && form.flow !== "{}") return true;
		if (form.settings && form.settings !== "{}") return true;
		if (form.isActive !== true) return true;
		return false;
	};

	const handleClose = () => {
		if (isCreate && hasUnsavedData()) {
			setConfirmCloseOpen(true);
		} else {
			onClose();
		}
	};

	const handleConfirmClose = () => {
		setConfirmCloseOpen(false);
		onClose();
	};

	useEffect(() => {
		if (open && !isView && channelsData) {
			const filtered = channelsData.filter((ch) => {
				return ch.platformId === 2 || ch.platform?.id === 2;
			});
			setWhatsappOptions(
				filtered.map((ch) => {
					return {
						key: ch.id,
						text: ch.label || "",
						value: ch.id,
					};
				})
			);
		}
	}, [open, isView, channelsData]);

	useEffect(() => {
		if (open) {
			if (flow && mode !== "create") {
				const init = {};
				for (const key of ["flowId", "isActive", "name", "label", "channelId", "endpoint_uri"]) {
					init[key] = flow[key] ?? "";
				}
				init.categories = parseCategories(flow.categories);
				init.settings = stringifyJson(flow.settings);
				init.flow = stringifyJson(flow.flow);
				setForm(init);
			} else {
				setForm(emptyFlow);
			}
			setError(null);
		}
	}, [open, flow, mode]);

	const handleChange = useCallback((e, { name, value }) => {
		setForm((prev) => {
			return { ...prev, [name]: value };
		});
	}, []);

	const handleEditorChange = useCallback((name, value) => {
		setForm((prev) => {
			return { ...prev, [name]: value || "" };
		});
	}, []);

	const handleSave = async () => {
		setError(null);
		if (!form.name || !form.name.trim()) {
			setError("El campo Nombre es obligatorio");
			notify("El campo Nombre es obligatorio", "danger");
			return;
		}
		if (!form.channelId) {
			setError("El campo Canal es obligatorio");
			notify("El campo Canal es obligatorio", "danger");
			return;
		}
		if (!Array.isArray(form.categories) || form.categories.length === 0) {
			setError("El campo Categorías es obligatorio");
			notify("El campo Categorías es obligatorio", "danger");
			return;
		}
		const jsonErr = validateJsonFields(form);
		if (jsonErr) {
			setError(jsonErr);
			notify(jsonErr, "danger");
			return;
		}
		setSaving(true);
		try {
			const payload = { ...form };
			payload.settings = JSON.parse(payload.settings || "{}");
			let flowJson;
			try {
				flowJson = JSON.parse(payload.flow || "{}");
			} catch {
				flowJson = {};
			}
			if (!flowJson.version || typeof flowJson.version !== "string") {
				flowJson.version = "5.0";
			}
			if (!Array.isArray(flowJson.screens)) {
				flowJson.screens = [];
			}
			payload.flow = flowJson;
			delete payload.flowId;
			const id = flow?.id;
			let result;
			if (isCreate) {
				const res = await Api.post(
					"/flows",
					pick(payload, ["name", "label", "channelId", "flow", "categories", "endpoint_uri", "settings", "isActive"])
				);
				result = res.data;
			} else {
				const res = await Api.patch(`/flows/${id}`, pick(payload, ["flow", "isActive", "label", "channelId"]));
				result = res.data;
			}
			if (result?.success === true) {
				notify(isCreate ? "Flow creado correctamente" : "Flow actualizado correctamente", "success");
				onSave();
				onClose();
			} else {
				setError(result?.message || "Error al guardar");
				notify(result?.message || "Error al guardar", "danger");
			}
		} catch (err) {
			const msg = err?.response?.data?.message || err?.response?.data?.error || err.message || "Error de conexión";
			setError(msg);
			notify(msg, "danger");
		} finally {
			setSaving(false);
		}
	};

	const title = isCreate ? "Nuevo Flow" : mode === "edit" ? "Editar Flow" : "Ver Flow";
	const headerTitle = isCreate ? title : `${title}: ${flow?.name || ""} - Id: ${flow?.id || ""} `;

	const cardStyle = {
		background: "#fff",
		borderRadius: 12,
		padding: 20,
		marginBottom: 16,
		border: "1px solid #e5e7eb",
		boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
	};

	const readOnlyFieldStyle = {
		background: "#f9fafb",
		border: "1px solid #e5e7eb",
		borderRadius: 8,
		padding: "10px 12px",
		fontSize: 14,
		color: "#6b7280",
	};

	const labelRequired = (text) => {
		return (
			<label style={{ fontSize: 15, fontWeight: 700, color: "#111827", marginBottom: 6, display: "block" }}>
				{text} <span style={{ color: "#ef4444" }}>*</span>
			</label>
		);
	};

	return (
		<>
			<Modal open={open} onClose={handleClose} closeIcon>
				<Modal.Header
					style={{
						backgroundColor: "var(--header)",
						color: "#fff",
						fontSize: 20,
						borderBottom: "none",
						display: "flex",
						alignItems: "center",
						gap: 8,
					}}>
					<Icon name="eye" style={{ marginRight: "0.2rem" }} />
					{headerTitle}
				</Modal.Header>
				<Modal.Content scrolling style={{ background: "var(--panelModal)", padding: 0 }}>
					{error && (
						<Message
							error
							onDismiss={() => {
								return setError(null);
							}}
							content={error}
							style={{ margin: "12px 16px", borderRadius: 10, fontSize: 13 }}
						/>
					)}
					<Form style={{ padding: 16 }}>
						<div style={cardStyle}>
							<div
								style={{
									display: "flex",
									alignItems: "center",
									justifyContent: "space-between",
									marginBottom: 16,
									paddingBottom: 10,
									borderBottom: "1px solid #f3f4f6",
								}}>
								<div style={{ display: "flex", alignItems: "center", gap: 8 }}>
									<div
										style={{
											width: 28,
											height: 28,
											borderRadius: 8,
											background: "linear-gradient(135deg, #3b82f6, #1d4ed8)",
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
										}}>
										<Icon name="info circle" style={{ color: "#fff", margin: 0, fontSize: 13 }} />
									</div>
									<span style={{ fontSize: 16, fontWeight: 700, color: "#111827" }}>Información General</span>
								</div>
								<div style={{ display: "flex", alignItems: "center", gap: 8 }}>
									<span style={{ fontSize: 13, fontWeight: 600, color: form.isActive ? "#166534" : "#991b1b" }}>
										{form.isActive ? "Activo" : "Inactivo"}
									</span>
									<Form.Checkbox
										toggle
										name="isActive"
										checked={form.isActive}
										onChange={(e, { name, checked }) => {
											return handleChange(e, { name, value: checked });
										}}
										disabled={isView}
									/>
								</div>
							</div>
							<Grid stackable>
								{!isCreate && (
									<Grid.Row columns={2} style={{ padding: 0 }}>
										<Grid.Column>
											<label style={{ fontSize: 15, fontWeight: 700, color: "#111827", marginBottom: 6, display: "block" }}>Flow ID</label>
											<Form.Input size="mini" name="flowId" value={form.flowId} readOnly className={styles.readOnlyInput} />
										</Grid.Column>
									</Grid.Row>
								)}
								<Grid.Row columns={2} style={{ padding: 0 }}>
									<Grid.Column>
										<Form.Input
											label={labelRequired("Nombre")}
											size="mini"
											name="name"
											value={form.name}
											onChange={handleChange}
											readOnly={!canEdit("name")}
											className={!canEdit("name") ? styles.readOnlyInput : ""}
										/>
									</Grid.Column>
									<Grid.Column>
										<Form.Input
											label="Label"
											size="mini"
											name="label"
											required
											value={form.label}
											onChange={handleChange}
											readOnly={!canEdit("label")}
											className={!canEdit("label") ? styles.readOnlyInput : ""}
										/>
									</Grid.Column>
								</Grid.Row>
								<Grid.Row columns={2} style={{ padding: 0 }}>
									<Grid.Column>
										<Form.Field>
											{labelRequired("Categorías")}
											{!canEdit("categories") ? (
												<div style={readOnlyFieldStyle}>
													{Array.isArray(form.categories) && form.categories.length > 0 ? form.categories.join(", ") : "—"}
												</div>
											) : (
												<Dropdown
													placeholder="Seleccionar categorías..."
													fluid
													multiple
													search
													selection
													name="categories"
													value={form.categories}
													onChange={(e, { name, value }) => {
														return handleChange(e, { name, value });
													}}
													options={CATEGORY_OPTIONS}
												/>
											)}
										</Form.Field>
									</Grid.Column>
								</Grid.Row>
								<Grid.Row columns={2} style={{ padding: 0 }}>
									<Grid.Column>
										<Form.Input
											size="mini"
											label="Endpoint URI"
											name="endpoint_uri"
											value={form.endpoint_uri}
											onChange={handleChange}
											readOnly={!canEdit("endpoint_uri")}
											className={!canEdit("endpoint_uri") ? styles.readOnlyInput : ""}
										/>
									</Grid.Column>
									<Grid.Column>
										{!canEdit("channelId") ? (
											<Form.Input
												label={labelRequired("Canal")}
												size="mini"
												value={
													whatsappOptions.find((o) => {
														return o.value === form.channelId;
													})?.text ||
													form.channelId ||
													""
												}
												readOnly
												className={styles.readOnlyInput}
											/>
										) : (
											<Form.Field>
												{labelRequired("Canal")}
												<Dropdown
													placeholder="Seleccionar canal..."
													fluid
													search
													selection
													name="channelId"
													value={form.channelId}
													onChange={(e, { name, value }) => {
														return handleChange(e, { name, value });
													}}
													options={whatsappOptions}
												/>
											</Form.Field>
										)}
									</Grid.Column>
								</Grid.Row>
							</Grid>
						</div>

						<div style={cardStyle}>
							<div
								style={{
									display: "flex",
									alignItems: "center",
									justifyContent: "space-between",
									marginBottom: 16,
									paddingBottom: 10,
									borderBottom: "1px solid #f3f4f6",
								}}>
								<div style={{ display: "flex", alignItems: "center", gap: 8 }}>
									<div
										style={{
											width: 28,
											height: 28,
											borderRadius: 8,
											background: "linear-gradient(135deg, #8b5cf6, #6d28d9)",
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
										}}>
										<Icon name="code" style={{ color: "#fff", margin: 0, fontSize: 13 }} />
									</div>
									<span style={{ fontSize: 16, fontWeight: 700, color: "#111827" }}>Flow</span>
								</div>
								{canEdit("flow") && (
									<Button
										icon="expand arrows alternate"
										size="mini"
										circular
										onClick={() => {
											return setEditorFullscreen(true);
										}}
										style={{ background: "transparent", color: "#6b7280" }}
									/>
								)}
							</div>
							<Form.Field>
								{!canEdit("flow") ? (
									<pre
										style={{
											background: "#f9fafb",
											color: "#374151",
											padding: 14,
											borderRadius: 10,
											fontSize: 12,
											maxHeight: 300,
											overflow: "auto",
											whiteSpace: "pre-wrap",
											border: "1px solid #e5e7eb",
											fontFamily: "'SF Mono', 'Fira Code', monospace",
											lineHeight: 1.5,
										}}>
										{form.flow || "—"}
									</pre>
								) : (
									<Editor
										height="500px"
										theme="vs-dark"
										defaultLanguage="json"
										value={form.flow}
										onChange={(val) => {
											return handleEditorChange("flow", val);
										}}
										options={{
											minimap: { enabled: false },
											fontSize: 12,
											fontFamily: "monospace",
											folding: true,
											scrollBeyondLastLine: false,
											automaticLayout: true,
										}}
									/>
								)}
							</Form.Field>
						</div>

						{editorFullscreen && (
							<div
								style={{
									position: "fixed",
									top: 0,
									left: 0,
									right: 0,
									bottom: 0,
									zIndex: 9999,
									background: "#1e1e1e",
									display: "flex",
									flexDirection: "column",
								}}>
								<div
									style={{
										display: "flex",
										alignItems: "center",
										justifyContent: "space-between",
										padding: "8px 16px",
										background: "#2d2d2d",
										borderBottom: "1px solid #3d3d3d",
									}}>
									<span style={{ color: "#ccc", fontSize: 13, fontWeight: 600 }}>Flow Editor</span>
									<Button
										icon="compress arrows alternate"
										size="mini"
										circular
										onClick={() => {
											return setEditorFullscreen(false);
										}}
										style={{ background: "transparent", color: "#ccc" }}
									/>
								</div>
								<div style={{ flex: 1 }}>
									<Editor
										height="100%"
										theme="vs-dark"
										defaultLanguage="json"
										value={form.flow}
										onChange={(val) => {
											return handleEditorChange("flow", val);
										}}
										options={{
											minimap: { enabled: true },
											fontSize: 12,
											fontFamily: "monospace",
											folding: true,
											scrollBeyondLastLine: false,
											automaticLayout: true,
										}}
									/>
								</div>
							</div>
						)}
					</Form>
				</Modal.Content>
				<Modal.Actions style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", height: "3.5rem" }}>
					<Button onClick={handleClose} className={`${styles.modalBtn} ${styles.modalBtnRed}`}>
						Cancelar
					</Button>
					{!isView && (
						<Button loading={saving} onClick={handleSave} className={`${styles.modalBtn} ${styles.modalBtnGray}`}>
							Guardar
						</Button>
					)}
				</Modal.Actions>
			</Modal>
			{confirmCloseOpen && (
				<ConfirmModal
					visible
					icon="exclamation circle"
					header="Confirmar Cerrar"
					buttonLabel="Cerrar"
					content="Tiene cambios sin guardar. ¿Está seguro que desea cerrar?"
					onConfirm={handleConfirmClose}
					onDismiss={() => {
						setConfirmCloseOpen(false);
					}}
				/>
			)}
		</>
	);
};

FlowModal.propTypes = {
	open: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	flow: PropTypes.shape({
		id: PropTypes.number,
		flowId: PropTypes.string,
		isActive: PropTypes.bool,
		name: PropTypes.string,
		channelId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		endpoint_uri: PropTypes.string,
		categories: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
		settings: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
		flow: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
	}),
	mode: PropTypes.oneOf(["view", "create", "edit"]).isRequired,
	onSave: PropTypes.func.isRequired,
	channelsData: PropTypes.array,
};

const WhatsAppFlowList = ({ channels, userName }) => {
	const [allFlows, setAllFlows] = useState([]);
	const [items, setItems] = useState([]);
	const [count, setCount] = useState(0);
	const [modalOpen, setModalOpen] = useState(false);
	const [modalMode, setModalMode] = useState(null);
	const [selectedFlow, setSelectedFlow] = useState(null);
	// const [openConfirmModal, setOpenConfirmModal] = useState(false);
	// const [tableValue, setTableValue] = useState(null);
	const [error, setError] = useState(null);
	const [filteredPrintData, setFilteredPrintData] = useState([]);
	const [statusFilter, setStatusFilter] = useState("active");
	const statusFilterRef = useRef("active");

	const [defaultParams, setDefaultParams] = useState({ page: 1, orderKey: "name", orderDir: "asc", rows: 10 });

	useEffect(() => {
		fetchFlows();
	}, []);

	useEffect(() => {
		if (allFlows.length > 0) {
			handlerData({ page: 1, orderKey: "name", orderDir: "asc", rows: 10 });
		}
	}, [allFlows]);

	const fetchFlows = async () => {
		setError(null);
		try {
			const res = await Api.get("/flows/table", { params: { rows: 15, page: 1, orderKey: "id", orderDir: "asc" } });
			const resData = res?.data?.data || res?.data || {};
			const flows = resData?.items || (Array.isArray(resData) ? resData : []);
			setAllFlows(flows);
		} catch (err) {
			setError(err?.response?.data?.message || "Error al cargar flows");
			notify(err?.response?.data?.message || "Error al cargar flows", "danger");
			setAllFlows([]);
		}
	};

	const getSearchQuery = (params) => {
		if (params.all) return params.all;
		const fieldKeys = (params.fields || []).filter((k) => {
			return k !== "acciones";
		});
		for (const key of fieldKeys) {
			if (params[key]) return params[key];
		}
		return "";
	};

	const SEARCHABLE_KEYS = ["name", "flowId", "endpoint_uri"];

	const handleStatusChange = (e, { value }) => {
		setStatusFilter(value);
		statusFilterRef.current = value;
		handlerData({ ...defaultParams, page: 1 });
	};

	const setRowStyle = (item) => {
		if (statusFilter === "all" && !item.isActive) {
			return { backgroundColor: "#ffcccc" };
		}
		if (statusFilter === "inactive" && !item.isActive) {
			return { backgroundColor: "rgba(255, 0, 0, 0.24)" };
		}
		return null;
	};

	const handlerData = async (params, printable) => {
		setDefaultParams((prev) => {
			return { ...prev, ...params };
		});
		let filtered = [...allFlows];

		const q = getSearchQuery(params).toLowerCase();
		if (q) {
			filtered = filtered.filter((f) => {
				const account = f.channel?.account || f.channel?.label || "";
				if (account.toLowerCase().includes(q)) return true;
				return SEARCHABLE_KEYS.some((key) => {
					const val = f[key];
					return val && val.toLowerCase().includes(q);
				});
			});
		}

		if (statusFilterRef.current === "active") {
			filtered = filtered.filter((f) => {
				return f.isActive === true;
			});
		} else if (statusFilterRef.current === "inactive") {
			filtered = filtered.filter((f) => {
				return f.isActive === false;
			});
		}

		if (params.orderKey) {
			filtered.sort((a, b) => {
				const aVal = (a[params.orderKey] || "").toString().toLowerCase();
				const bVal = (b[params.orderKey] || "").toString().toLowerCase();
				if (params.orderDir === "desc") return bVal.localeCompare(aVal);
				return aVal.localeCompare(bVal);
			});
		}

		const start = (params.page - 1) * params.rows;
		const sliced = filtered.slice(start, start + params.rows);

		if (printable) {
			setFilteredPrintData(filtered);
		} else {
			setItems(sliced);
		}
		setCount(filtered.length);
	};

	const modalHandler = (name, item) => {
		if (name === "Add") {
			setSelectedFlow(null);
			setModalMode("create");
			setModalOpen(true);
		} else if (name === "view" || name === "Watch") {
			setSelectedFlow(item);
			setModalMode("view");
			setModalOpen(true);
		} else if (name === "edit") {
			setSelectedFlow(item);
			setModalMode("edit");
			setModalOpen(true);
			// } else if (name === "delete") {
			// 	setTableValue(item);
			// 	setOpenConfirmModal(true);
			// }
		}

		// const handleDelete = async () => {
		// if (!tableValue) return;
		// try {
		// 	const res = await Api.delete(`/flows/${tableValue.id}`);
		// 	if (res?.data?.success === true) {
		// 		notify("Flow actualizado correctamente", "success");
		// 		fetchFlows();
		// 	} else {
		// 		setError(res?.data?.message || "Error al eliminar");
		// 		notify(res?.data?.message || "Error al eliminar", "danger");
		// 	}
		// } catch (err) {
		// 	setError(err?.response?.data?.message || "Error al eliminar");
		// 	notify(err?.response?.data?.message || "Error al eliminar", "danger");
		// } finally {
		// 	setOpenConfirmModal(false);
		// 	setTableValue(null);
		// }
	};

	const columns = [
		{
			label: "Acciones",
			key: "acciones",
			format: "string",
			width: 1,
			headerPosition: "center",
			dataPosition: "center",
			action: [
				// {
				// 	type: "icon",
				// 	content: "trash",
				// 	onClick: "delete",
				// 	color: "#b43333",
				// 	visible: (rowData) => {
				// 		return rowData.isActive !== false;
				// 	},
				// },
				{
					type: "icon",
					content: "edit",
					onClick: "edit",
					color: "#3f7ea3",
				},
			],
		},
		{
			label: "ID",
			key: "id",
			format: "string",
			headerPosition: "center",
			dataPosition: "center",
			width: 1,
		},
		{
			label: "Flow ID",
			key: "flowId",
			format: "string",
			headerPosition: "center",
			dataPosition: "center",
			width: 1,
		},
		{
			label: "Nombre",
			key: "name",
			format: "string",
			headerPosition: "left",
			width: 1,
		},
		{
			label: "Label",
			key: "label",
			format: "string",
			headerPosition: "left",
			width: 1,
		},
		{
			label: "Cuenta",
			key: "channelId",
			format: "string",
			headerPosition: "center",
			dataPosition: "center",
			width: 1,
			render: (rowData) => {
				return rowData.channel?.label || rowData.channel?.account || rowData.channelId || "—";
			},
		},
		{
			label: "Categorías",
			key: "categories",
			format: "truncate",
			stringLength: 20,
			headerPosition: "left",
			width: 1,
		},
		{
			label: "Endpoint URI",
			key: "endpoint_uri",
			format: "truncate",
			stringLength: 20,
			headerPosition: "left",
			width: 10,
		},
	];

	return (
		<Screen hasHeader hasSidebar headerTitle="Flow Editor">
			<div className={styles.container}>
				{error && (
					<Message
						error
						onDismiss={() => {
							return setError(null);
						}}
						content={error}
						style={{ width: "100%", margin: 0, borderRadius: 0 }}
					/>
				)}

				<div className={styles.table}>
					<Segment style={{ margin: "0 auto", borderRadius: 12, boxShadow: "0 2px 12px rgba(0,0,0,0.08)" }}>
						<IkTable
							data={items}
							filteredPrintData={filteredPrintData}
							columns={columns}
							totalRows={count}
							defaultParams={defaultParams}
							dataHandler={handlerData}
							handlerTable={modalHandler}
							setRowStyle={setRowStyle}
							addButton={{ icon: "plus", label: "Agregar" }}
							searcher={{
								searchable: true,
								filterButton: false,
								width: "40rem",
							}}
							topButtons={[
								{
									componentType: "DropDown",
									value: statusFilter,
									options: [
										{ key: "all", value: "all", text: "Todos" },
										{ key: "active", value: "active", text: "Activos" },
										{ key: "inactive", value: "inactive", text: "Inactivos" },
									],
									onChange: handleStatusChange,
								},
							]}
							print={{
								printHeader: {
									company: "WhatsApp Flows",
									title: "Listado de Flows",
									user: userName,
								},
								visiblePDF: true,
								visibleExcel: true,
							}}
						/>
					</Segment>
				</div>

				<FlowModal
					open={modalOpen}
					onClose={() => {
						return setModalOpen(false);
					}}
					flow={selectedFlow}
					mode={modalMode}
					onSave={fetchFlows}
					channelsData={channels}
				/>

				{/* {openConfirmModal && (
				<ConfirmModal
					visible
					icon="exclamation circle"
					header="Confirmar Eliminar"
					buttonLabel="Eliminar"
					content="Está seguro que desea eliminar el Flow"
					focus={tableValue?.name}
					onConfirm={handleDelete}
					onDismiss={() => {
						setOpenConfirmModal(false);
						setTableValue(null);
					}}
				/>
			)} */}
			</div>
		</Screen>
	);
};

WhatsAppFlowList.propTypes = {
	channels: PropTypes.array,
	userName: PropTypes.string,
};

const mapStateToProps = (state) => {
	return {
		channels: state.company.channels,
		userName: state.user.name,
	};
};

export default connect(mapStateToProps)(WhatsAppFlowList);
